// src/properties/propertiesApi.ts
import { apiClient } from "@maat/api-base";
var propertiesApi = {
  getProperties: async (filters) => {
    return apiClient.get("/main/api/properties", { params: filters });
  },
  // Rooms blocked / booked / pending / held / to sell per room type per day — the
  // availability half of the rates & availability grid. CalendarView-gated.
  getAvailabilityGrid: async (propertyId, params) => {
    return apiClient.get(`/main/api/properties/${propertyId}/availability-grid`, { params });
  },
  getPropertiesInPolygon: async (geometryId, filters) => {
    return apiClient.get(`/main/api/properties/in-polygon/${geometryId}`, { params: filters });
  },
  /**
   * `units` answers every room type in REQUEST order; `unitId` / `listingId` are the first.
   * Several room types, or any rooms, 503 `FEATURE_DISABLED` while the multi-unit gate is off.
   */
  createProperty: async (data) => {
    return apiClient.post("/main/api/properties", data);
  },
  /** The property's own name and/or description — at least one. */
  updateProperty: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}`, body);
  },
  getGuestFeed: async (params) => {
    return apiClient.get("/main/api/properties/guest/feed", { params });
  },
  getGuestHome: async () => {
    return apiClient.get("/main/api/properties/guest/home");
  },
  getGuestHomePersonal: async () => {
    return apiClient.get("/main/api/properties/guest/home/personal");
  },
  getMapProperties: async (filter) => {
    const { bbox, amenities, ...rest } = filter;
    return apiClient.get("/main/api/properties/map", {
      params: {
        ...rest,
        bbox: bbox.join(","),
        // minLng,minLat,maxLng,maxLat
        ...amenities && amenities.length ? { amenities } : {}
      },
      paramsSerializer: { indexes: null }
      // repeats: amenities=a&amenities=b
    });
  },
  getMapPropertiesList: async (filter) => {
    const { bbox, amenities, ...rest } = filter;
    return apiClient.get("/main/api/properties/map/list", {
      params: {
        ...rest,
        bbox: bbox.join(","),
        ...amenities && amenities.length ? { amenities } : {}
      },
      paramsSerializer: { indexes: null }
    });
  },
  /**
   * The relaxed-search rails for the same viewport — takes the identical filter
   * the map and its list take, so a caller can pass the one object it already
   * built. The server decides which rails it can fill.
   */
  getMapSuggestions: async (filter) => {
    const { bbox, amenities, ...rest } = filter;
    return apiClient.get("/main/api/properties/map/suggestions", {
      params: {
        ...rest,
        bbox: bbox.join(","),
        ...amenities && amenities.length ? { amenities } : {}
      },
      paramsSerializer: { indexes: null }
    });
  },
  /**
   * The price distribution behind the range filter's histogram, off the same
   * filter object. `minPrice`/`maxPrice` are sent and IGNORED server-side — a
   * facet must be excluded from its own aggregation — and stay in the request
   * only so this call can take `MapPropertyFilter` as-is.
   */
  getMapPriceDistribution: async (filter) => {
    const { bbox, amenities, ...rest } = filter;
    return apiClient.get("/main/api/properties/map/price-distribution", {
      params: {
        ...rest,
        bbox: bbox.join(","),
        ...amenities && amenities.length ? { amenities } : {}
      },
      paramsSerializer: { indexes: null }
    });
  },
  /**
   * "More stays nearby" for one listing page. The only property read here that
   * takes NO viewport — the rectangle is derived server-side from the listing's
   * own point. `filter` is optional so an undated page still gets a rail;
   * passing the guest's dates is what turns the prices into stay totals.
   */
  getNearbyListings: async (listingId, filter) => {
    return apiClient.get(`/main/api/properties/nearby/${listingId}`, { params: filter });
  },
  /**
   * The guest property page for a property with several listings. A property with ONE
   * live listing answers only `single_listing_id` — open that listing's page instead.
   */
  getPublicProperty: async (propertyId, params) => {
    return apiClient.get(`/main/api/properties/${propertyId}/public`, { params });
  },
  /**
   * Free-text suggestions for the guest Search tab — the one property call that
   * takes words instead of a viewport, so no `bbox`. Callers debounce; the
   * server rejects anything under 3 characters, which the area lookup behind it
   * cannot match on.
   */
  getSearchSuggest: async (q) => {
    return apiClient.get("/main/api/properties/search-suggest", { params: { q } });
  },
  getHostProperties: async (filters) => {
    return apiClient.get("/main/api/properties/host", {
      params: filters,
      paramsSerializer: { indexes: null }
      // repeats: property_type[]=a&property_type[]=b
    });
  },
  getHostPropertyCities: async () => {
    return apiClient.get("/main/api/properties/host/cities");
  },
  getPropertyDetail: async (propertyId) => {
    return apiClient.get(`/main/api/properties/${propertyId}/detail`);
  },
  patchPropertyAmenities: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}/amenities`, body);
  },
  /** Replace the property's OWN amenities (a hotel's facilities); `[]` clears them. */
  patchPropertyFacilities: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}/facilities`, body);
  },
  /**
   * Replace the unit's house-rule answers. `body.houseRules` carries one entry
   * per rule the host was SHOWN, unticked ones included (`allowed: false`) —
   * sending only the allowed ones makes a missing entry ambiguous between "said
   * no" and "never asked".
   */
  patchPropertyHouseRules: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}/house-rules`, body);
  },
  patchPropertyPricing: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}/pricing`, body);
  },
  getListingImages: async (propertyId, ctx) => {
    return apiClient.get(`/main/api/properties/${propertyId}/images`, {
      params: { asset: "listing", unit_id: ctx.unitId, listing_id: ctx.listingId }
    });
  },
  /** The HOTEL's own gallery (property-owned photos only), every processing state. */
  getPropertyGalleryImages: async (propertyId) => {
    const params = { asset: "property" };
    return apiClient.get(`/main/api/properties/${propertyId}/images`, { params });
  },
  /** A ROOM TYPE's own gallery — never the hotel's photos (README H18). */
  getUnitGalleryImages: async (propertyId, unitId) => {
    const params = { asset: "unit", unit_id: unitId, scope: "own" };
    return apiClient.get(`/main/api/properties/${propertyId}/images`, { params });
  },
  /**
   * Applies gallery edits and returns one presigned POST per new photo. Deletes
   * and the reorder are applied server-side before any sort order is allocated,
   * and the bytes never touch our API. A 503 (direct uploads off) is a STOP —
   * the multipart route is retired, so there is no fallback.
   */
  presignPropertyImageEdits: async (propertyId, body) => {
    return apiClient.post(`/main/api/properties/${propertyId}/images/presign`, body);
  },
  resolveArea: async (lat, lng) => {
    return apiClient.post("/main/api/areas/resolve", { lat, lng });
  },
  // Host flags an AI-declined image as a possible mistake (admin reviews it).
  flagImage: async (propertyId, imageId, body) => {
    return apiClient.post(`/main/api/properties/${propertyId}/images/${imageId}/flag`, body);
  },
  /**
   * Reserves rows and returns one presigned POST per declared file. The client
   * uploads straight to S3; the bytes never pass through our API. A 503 (direct
   * uploads off) is a STOP — the multipart route is retired, so there is no
   * fallback.
   */
  /**
   * The host's own publish switch. Returns the RESOLVED state: publishing a listing
   * an admin has not approved is accepted and changes nothing a guest can see, so the
   * client must render what comes back rather than what it asked for.
   */
  setListingPublication: async (propertyId, body) => {
    return apiClient.patch(`/main/api/properties/${propertyId}/listing/publication`, body);
  },
  presignPropertyMedia: async (propertyId, body) => {
    return apiClient.post(`/main/api/properties/${propertyId}/media/presign`, body);
  }
};

export {
  propertiesApi
};
